import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { FormControl } from '@angular/forms';

import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';

import { MatAutocompleteTrigger, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import { CommandRelay } from '../../commands/command-relay.class';

@Component({
	selector: 'multi-filterable-select',
	templateUrl: 'multi-filterable-select.html',
	styleUrls: ['multi-filterable-select.css']
})
export class MultiFilterableSelectComponent {
	// Le composant de saisie, vu comme un FormControl, pour manipulation de son contenu
	filterInput = new FormControl();

	// Le composant de saisie, vu comme un trigger pour la manipulation de la dropdown list
	@ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger }) input!: MatAutocompleteTrigger;

	// Parametrage du composant
	@Input() id!: string;
	@Input() label!: string;

	itemList: any[] = [];
	selections: boolean[] = [];
	@Input() set list(values: any[]) {
		if (values) {
			this.itemList = [...values];
			this.selections = new Array(this.itemList.length).fill(false);
			this.showSelect = this.itemList.length > 0;
		} else {
			this.itemList = [];
			this.selections = [];
			this.showSelect = false;
		}
	}
	get list(): any[] {
		return this.itemList;
	}

	@Input() labelGetter!: (item: any) => string;
	
	private changeSelectionCommandsRelay = new CommandRelay<(item: any, isSelected: boolean) => boolean>((selectionFunction) => this.changeSelection(selectionFunction));
	@Input() set changeSelectionCommands(obs: Observable<(item: any, isSelected: boolean) => boolean>) {
		this.changeSelectionCommandsRelay.register(obs);
	}

	// TODO: il faudra sans doute aussi permettre un input sur selectedItems plus tard
	@Output() selectedItemsChange = new EventEmitter<any[]>();

	showSelect: boolean = true;

	// A cause de divers problemes poses par la maniere dont angular reagit a l'ajout / retrait des options, il faut remettre la valeur du select a null a chaque fois
	selectedItem: number | null = null;

	// La liste actuelle des elements filtres de la drop-down
	filteredItems!: Observable<any[]>;

	// Fonction simplifiant l'utilisation du labelGetter dans l'interface
	getLabel: (item: any) => string = (item) => item ? this.labelGetter(item) : '';

	constructor() {
		this.bindList();
	}
	
	private changeSelection(selectionFunction: (item: any, isSelected: boolean) => boolean) {
		for(let i = 0; i < this.itemList.length; i++) {
			let item = this.itemList[i];
			let oldState = this.selections[i];
			
			let newState = selectionFunction(item, oldState);
			
			this.selections[i] = newState; 
		}
	}

	onInputFocus() {
		// Lorsque le focus arrive sur le composant, on reinitialise la liste filtree.
		// En effet, le contenu de la liste source peut avoir change meme si sa reference est identique
		this.bindList();
	}

	onInputBlur() {
		// Quand on quitte la zone, il se peut que l'utilisateur aie saisi un filtre mais aie quitte sans faire de selection.
		// Dans un tel cas, la zone continue a afficher la saisie, et il faut le retirer
		//
		// Cependant, selectionner une valeur dans la liste par un clic provoque aussi la sortie de la zone, et le reset de celle-ci risque alors d'annuler la selection.
		// Pour eviter ceci, on delaie le reset automatique, afin que si une selection par clic est en cours, elle puisse s'éxcuter avant.
		setTimeout( //
			() => this.resetInputValue(),
			300
		);
	}

	onInputClick() {
		// Contrairement a un <select> la liste ne s'ouvre pas naturellement quand on clique sur le champ apres la selection d'une valeur
		// Il faut donc le faire programativement
		this.ensureDropdownOpen();
	}

	onInputKeyUp(event: any) {
		const key = event.key;
		if (key === 'Enter') {
			// Meme probleme que pour l'evenement click ci-dessus, mais dans le cas de l'utilisation de la touche 'Enter'
			this.ensureDropdownOpen();
		} else if (key === 'Escape') {
			// Si l'utilisateur appuie sur Esc alors que la liste est ouverte, elle se ferme.
			// Cependant, la saisie qu'il a eventuellement fait demeure alors.
			// Il faut donc la vider programmativement a ce moment
			this.resetInputValue();
		}
	}

	onOptionSelected(event: MatAutocompleteSelectedEvent) {
		// Quand un item est selectionne dans la dropdown list, il faut mettre a jour la donnee du modele
		// Aussi, il est necessaire de lancer les evenements relatifs, pour que le parent se rende compte du changement
		const selectedItem = event.option.value;
		if (selectedItem) {
			const index = selectedItem.index;
			this.selections[index] = true;
			this.showSelect = this.selections.filter((value) => !value).length > 0;
			this.resetInputValue();
			this.emitSelectionChange();
		}
	}

	onDeselectItem(index: number) {
		this.selections[index] = false;
		this.showSelect = true;
		this.emitSelectionChange();
	}

	onSelectAll() {
		this.selections.fill(true);
		this.showSelect = false;
		this.emitSelectionChange();
		this.selectedItem = null;
	}

	onDeselectAll() {
		this.selections.fill(false);
		this.showSelect = true;
		this.emitSelectionChange();
	}

	private emitSelectionChange() {
		const selection = this.itemList //
			.filter(
				(_item: any, index: number) => this.selections[index]
			);
		this.selectedItemsChange.emit(selection);
	}

	// S'assure que le drowndown list est ouverte
	private ensureDropdownOpen() {
		if (!this.input.panelOpen) {
			this.input.openPanel();
		}
	}

	// (Re-)cree une relation automatique entre la valeur du filtre et la liste affichee
	private bindList() {
		this.filteredItems = this.filterInput.valueChanges.pipe(
			startWith(''),
			// Quand on fixe la valeur de l'input de facon programmative, ce pipe est declenche avec l'item en
			// pametre avant que la directive 'displayWith' le transforme en string.
			// Afin d'empecher des erreurs dans la fonction filterList, on realise une preconversion de la valeur du filtre en string.
			map((value: any) => this.toString(value)),
			map((value: string) => this.filterList(value))
		);
	}

	// Verifie si une valeur est de type string, sinon renvoie ''
	private toString(value: any): string {
		if (value === undefined) {
			return '';
		}

		const type = typeof value;
		if (type === "string") {
			return value;
		}

		return '';
	}

	// Renvoie la liste des valeurs de la liste qui correspondent au filtre passe en parametre
	private filterList(filterValue: string): any[] {

		if (!this.list) {
			return [];
		}

		// 1- Transformer les elements pour assurer la preservation de leur index durant le filtrage
		let filteredList = this.itemList.map(
			(item: any, index: number) => {
				return { index: index, entity: item };
			}
		);

		// 2- Filtrer les elements deja selectionnes
		filteredList = filteredList.filter(
			(_item: any, index: number) => {
				return !this.selections[index];
			}
		);

		// 3- Filtrer ensuite les elements qui ne correspondent pas au filtrer saisi
		if (filterValue) {
			const filter = this.normalizeValue(filterValue);
			filteredList = filteredList.filter(
				(item) => {
					let label: string | undefined = this.getLabel(item.entity);
					let normalizedLabel: string = this.normalizeValue(label);
					return normalizedLabel.includes(filter);
				}
			);
		}

		return filteredList;
	}

	// Normalise un string pour comparaison sans etre genee par la casse ou certains caracteres speciaux
	private normalizeValue(value: string | undefined): string {
		if (!value) {
			return '';
		}
		return value.toLowerCase().replace(/\s/g, '');
	}

	// Vide le champ de saisie
	private resetInputValue() {
		this.filterInput.setValue('');
	}
}